import { useState, useEffect } from 'react';
import { TALENT, TALENT_ARR } from '../../data/define';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { SELECT_STYLE } from '../../data/define';
import IcTriangle10 from '../../assets/image/triangle/ic_triangle_10.svg';
import IcTriangle25 from '../../assets/image/triangle/ic_triangle_25.svg';
import IcTriangle50 from '../../assets/image/triangle/ic_triangle_50.svg';
import IcTriangle75 from '../../assets/image/triangle/ic_triangle_75.svg';
import IcTriangle90 from '../../assets/image/triangle/ic_triangle_90.svg';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { useTranslation } from '../../hook/useTranslation';

const TRIANGLE_STATUS = {
  VERY_HIGH: 1,
  HIGH: 2,
  MIDDLE: 3,
  LOW: 4,
  VERY_LOW: 5,
};

const TRIANGLE_POSITION = {
  VERY_HIGH: 'translateY(-290%)',
  HIGH: 'translateY(-180%)',
  MIDDLE: 'translateY(-87%)',
  LOW: 'translateY(20%)',
  VERY_LOW: 'translateY(120%)',
};

function IntelligenceRankTriange({ result = [] }) {
  const { t } = useTranslation();
  const [textYPosition, setTextYPosition] = useState(TRIANGLE_POSITION.VERY_HIGH);
  const [intelligence, setIntelligence] = useState(1);
  const [kidScore, setKidScore] = useState(0);
  const [triangleCase, setTriangleCase] = useState(TRIANGLE_STATUS.VERY_HIGH);
  const [editedResult, setEditedResult] = useState([]);

  useEffect(() => {
    if (result?.length > 0) {
      let totalResult = [];
      TALENT_ARR.forEach((talent) => {
        const existItem = result.find((item) => item.id === talent.id);
        if (existItem) {
          totalResult.push(existItem);
        } else {
          totalResult.push({
            id: talent.id,
            percentile: 0,
          });
        }
      });
      setEditedResult(totalResult);
    }
  }, [result]);

  // 지능순위 default 설정
  useEffect(() => {
    if (result && result?.length > 0) {
      const defaultResult = result.reduce((prev, current) => {
        return (prev.percentile || 0) > (current.percentile || 0) ? current : prev;
      });
      const defaultTalent = TALENT_ARR.find((item) => item.id === defaultResult.id);
      if (defaultTalent) {
        setIntelligence(defaultTalent.id);
      } else {
        setIntelligence(TALENT.RELATIONSHIP.id);
      }
    } else {
      setIntelligence(TALENT.RELATIONSHIP.id);
    }
  }, [result]);

  // 지능에 따른 아이의 percentile 점수 설정
  useEffect(() => {
    if (result && result?.length > 0 && intelligence) {
      const intelligenceResult = result?.find((item) => item.id === intelligence);
      if (intelligenceResult) {
        setKidScore(intelligenceResult?.percentile);
      } else {
        setKidScore(0);
      }
    }
  }, [intelligence, result]);

  // 삼각형 결정
  useEffect(() => {
    switch (true) {
      case kidScore <= 10:
        setTriangleCase(TRIANGLE_STATUS.VERY_HIGH);
        break;
      case kidScore > 10 && kidScore <= 25:
        setTriangleCase(TRIANGLE_STATUS.HIGH);
        break;
      case kidScore > 25 && kidScore <= 50:
        setTriangleCase(TRIANGLE_STATUS.MIDDLE);
        break;
      case kidScore > 50 && kidScore <= 75:
        setTriangleCase(TRIANGLE_STATUS.LOW);
        break;
      case kidScore > 75:
        setTriangleCase(TRIANGLE_STATUS.VERY_LOW);
        break;
      default:
        setTriangleCase(TRIANGLE_STATUS.VERY_HIGH);
        break;
    }
  }, [kidScore]);

  // '아이' 포지션 결정
  useEffect(() => {
    switch (true) {
      case kidScore <= 10:
        setTextYPosition(TRIANGLE_POSITION.VERY_HIGH);
        break;
      case kidScore > 10 && kidScore <= 25:
        setTextYPosition(TRIANGLE_POSITION.HIGH);
        break;
      case kidScore > 25 && kidScore <= 50:
        setTextYPosition(TRIANGLE_POSITION.MIDDLE);
        break;
      case kidScore > 50 && kidScore <= 75:
        setTextYPosition(TRIANGLE_POSITION.LOW);
        break;
      case kidScore > 75:
        setTextYPosition(TRIANGLE_POSITION.VERY_LOW);
        break;
      default:
        setTextYPosition(TRIANGLE_POSITION.VERY_HIGH);
        break;
    }
  }, [kidScore]);

  const getDescriptionKey = () => {
    switch (triangleCase) {
      case TRIANGLE_STATUS.VERY_HIGH:
        return 'report.intelligence_rank.desc_vhigh';
      case TRIANGLE_STATUS.HIGH:
        return 'report.intelligence_rank.desc_high';
      case TRIANGLE_STATUS.MIDDLE:
        return 'report.intelligence_rank.desc_middle';
      case TRIANGLE_STATUS.LOW:
        return 'report.intelligence_rank.desc_low';
      case TRIANGLE_STATUS.VERY_LOW:
        return 'report.intelligence_rank.desc_vlow';
      default:
        return 'report.intelligence_rank.desc_vhigh';
    }
  };

  return (
    <div className="relative w-full pt-[20px] pb-[20px]">
      <div className="w-full flex justify-between items-center mb-[20px]">
        <div className="text-[#1C1C1C] font-[700] text-[20px] text-left">{t('report.intelligence_rank.title')}</div>
        <Select
          variant="outlined"
          value={intelligence || ''}
          onChange={(e) => setIntelligence(e.target.value)}
          IconComponent={KeyboardArrowDownIcon}
          size="small"
          sx={{
            ...SELECT_STYLE,
            height: '38px',
            borderRadius: '5px',
            border: '1px solid #E0E0E0',
            minWidth: '110px',
            fontWeight: 400,
          }}
        >
          {editedResult?.length >= 1 &&
            editedResult?.map((intelli) => {
              const target = TALENT_ARR.find((item) => item.id === intelli.id);
              return (
                <MenuItem
                  key={intelli.id}
                  value={intelli.id}
                  sx={{ fontSize: '14px' }}
                  disabled={intelli.percentile === 0}
                >
                  <span className="text-[14px] text-[#363636]">
                    {target ? t('talent.' + target.key) : ''}
                  </span>
                </MenuItem>
              );
            })}
        </Select>
      </div>
      <div className="flex flex-col items-start justify-start w-full text-[#767676] text-[14px] font-[400]">
        <div>{t('report.intelligence_rank.age_group')}</div>
        <div>
          <span className="text-[#42A9AA] font-[700] text-[20px]">
            {t('report.intelligence_rank.top_percent', { percent: kidScore })}
          </span>
          <span>{t('report.intelligence_rank.belong_to')}</span>
        </div>
        <div className="relative w-full h-[200px] mb-[-30px] flex items-center justify-center overflow-visible">
          {triangleCase === TRIANGLE_STATUS.VERY_HIGH && (
            <img src={IcTriangle10} alt="ic_triangle_vhigh" />
          )}
          {triangleCase === TRIANGLE_STATUS.HIGH && (
            <img src={IcTriangle25} alt="ic_triangle_high" />
          )}
          {triangleCase === TRIANGLE_STATUS.MIDDLE && (
            <img src={IcTriangle50} alt="ic_triangle_middle" />
          )}
          {triangleCase === TRIANGLE_STATUS.LOW && <img src={IcTriangle75} alt="ic_triangle_low" />}
          {triangleCase === TRIANGLE_STATUS.VERY_LOW && (
            <img src={IcTriangle90} alt="ic_triangle_very_low" />
          )}
          <div
            style={{ transform: textYPosition }}
            className="absolute left-[50%] translate-x-[-440%] text-[10px] text-[#fff] font-[400] rounded-[8px] bg-[#363636] px-[5px] py-[4px]"
          >
            {t('report.intelligence_rank.kid_label')}
          </div>
        </div>
        <div className="text-[#363636] text-left" style={{ whiteSpace: 'pre-line' }}>
          {t(getDescriptionKey())}
        </div>
      </div>
    </div>
  );
}

export default IntelligenceRankTriange;

